import { secondAxios } from "../../../../../api/axios";
import {
  isEstimationProjectType,
  projectTypeDiscipline,
} from "../../../../../constants/projectTypes";
import { revisionDocumentUID } from "../../../../../utils/revisionUID";

/**
 * WBS (project) records. Each function does its HTTP call(s) and returns;
 * payload shaping and cache updates stay with the caller.
 */
export const createWbsRecord = async ({
  projectName,
  clientName,
  link,
  description,
  type,
  plantId,
}) => {
  const response = await secondAxios.post(`/project/create/${clientName}`, {
    projectName,
    clientName,
    link,
    description,
    type,
    plantId,
  });
  return response?.data;
};

// PATCH '/project/:id/name-type' rather than PATCH '/project/:id': the
// latter is declared twice in the backend's project controller and the
// first declaration (assignProjectUsers) is the one that matches, so a
// { name } body never reaches the update handler.
export const renameWbsRecord = async (wbsId, changes) => {
  await secondAxios.patch(`/project/${wbsId}/name-type`, changes);
};

export const deleteWbsRecord = async (wbsId) => {
  await secondAxios.delete(`/project/${wbsId}`);
};

/**
 * Gives a freshly created WBS its Rev 0, so it never sits in the tree with no
 * revision to open - the modules used to create that first revision only when
 * the WBS was opened (Piping) or first saved (PV).
 *
 * Where a revision lives differs by discipline: PV keeps its revisions as
 * keys of the PV/all component's `inputData`, while the Estimation modules
 * keep theirs as keys of a revision row's `estimationTable`. Spec WBS have no
 * revisions at all, so they get nothing.
 */
export const seedFirstRevision = async (
  projectId,
  type,
  { plantName, wbsName } = {},
) => {
  if (!projectId) return;

  const discipline = projectTypeDiscipline(type);

  try {
    if (discipline === "pv") {
      // Seeded with the same Input Data document UID savePVData would write
      // ({PLANT}-{WBS}-R0), so the tree shows this revision under its real
      // name from the start instead of the bare "Rev 0" placeholder.
      await secondAxios.post(`project/component/${projectId}/PV/all`, {
        inputData: {
          0: { documentUID: revisionDocumentUID(plantName, wbsName, 0) },
        },
      });
      return;
    }

    if (isEstimationProjectType(type)) {
      // Same Rev UID savePipingData / saveStructuralData would write, for
      // the same reason as PV's above.
      await secondAxios.post(`/project/${projectId}/rev`, {
        name: "0",
        data: {
          estimationTable: {
            0: {
              documentUID: revisionDocumentUID(plantName, wbsName, 0),
            },
          },
        },
      });
    }
  } catch (error) {
    // Not worth failing the WBS creation over: the module still creates its
    // own Rev 0 the first time the WBS is opened or saved, exactly as before.
    console.log("Failed to create the first revision", error);
  }
};
